import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraFormField, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/authContext';
import { contractsClient } from '../../Delivery/Registries/Contracts/api';
export function AwardSignalsEntry() {
    const [params] = useSearchParams();
    const { user } = useAuth();
    const id = params.get('watchOpportunity');
    if (!id || !user?.tenant_id || !user.id) return null;
    return /*#__PURE__*/ _jsx(AwardSignals, {
        id: id,
        tenant: user.tenant_id,
        viewer: user.id
    }, `${user.tenant_id}:${user.id}:${id}`);
}
function AwardSignals({ id, tenant, viewer }) {
    const qc = useQueryClient();
    const key = [
        'awardSignal',
        tenant,
        viewer,
        id
    ];
    const signal = useQuery({
        queryKey: key,
        queryFn: ()=>contractsClient.getAwardSignal({
                opportunityId: id
            }),
        retry: false
    });
    if (signal.isPending) return /*#__PURE__*/ _jsx("output", {
        "data-testid": Testids.awardSignal.status,
        children: "Loading award evidence…"
    });
    if (signal.error) return signal.error instanceof ConnectError && signal.error.code === Code.NotFound ? null : /*#__PURE__*/ _jsxs("p", {
        role: "alert",
        children: [
            "Award evidence unavailable. Access may have changed.",
            ' ',
            /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>void signal.refetch(),
                children: "Retry"
            })
        ]
    });
    if (!signal.data.signal) return null;
    return /*#__PURE__*/ _jsx(AwardSignalDetail, {
        signal: signal.data.signal,
        saved: ()=>qc.invalidateQueries({
                queryKey: key
            })
    });
}
export function AwardSignalDetail({ signal: s, saved }) {
    const [date, setDate] = useState(s.teamTimingHypothesis ?? '');
    const [note, setNote] = useState(s.hypothesisNote);
    const save = useMutation({
        mutationFn: ()=>contractsClient.updateAwardSignalHypothesis({
                opportunityId: s.opportunityId,
                date,
                note
            }),
        onSuccess: saved
    });
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": Testids.awardSignal.panel,
        "data-opportunity-id": s.opportunityId,
        className: "space-y-3 border rounded p-4",
        "aria-label": "Award and recompete signals",
        children: [
            /*#__PURE__*/ _jsx("h3", {
                children: "Award and recompete signals"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Historical award — research context, not an open bid target."
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Predecessor:",
                    ' ',
                    /*#__PURE__*/ _jsx(Link, {
                        to: `/registries?tab=contracts&record=${encodeURIComponent(s.contractId)}`,
                        children: s.contractIdentity
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Parent: ",
                    s.parentIdentity || 'Unknown'
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Incumbent: ",
                    s.incumbentName || 'Unknown'
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Coverage:",
                    ' ',
                    s.coverage === 'incomplete' ? 'Incomplete — manual signal remains usable' : 'Selected completion evidence only; other award coverage may be incomplete'
                ]
            }),
            /*#__PURE__*/ _jsxs("table", {
                "data-testid": Testids.awardSignal.dates,
                children: [
                    /*#__PURE__*/ _jsx("thead", {
                        children: /*#__PURE__*/ _jsxs("tr", {
                            children: [
                                /*#__PURE__*/ _jsx("th", {
                                    children: "Observed current completion"
                                }),
                                /*#__PURE__*/ _jsx("th", {
                                    children: "Observed ultimate completion"
                                }),
                                /*#__PURE__*/ _jsx("th", {
                                    children: "Potential recompete horizon (derived)"
                                }),
                                /*#__PURE__*/ _jsx("th", {
                                    children: "Team timing hypothesis"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx("tbody", {
                        children: /*#__PURE__*/ _jsxs("tr", {
                            children: [
                                /*#__PURE__*/ _jsx("td", {
                                    children: s.observedCurrentCompletion || 'Unknown'
                                }),
                                /*#__PURE__*/ _jsx("td", {
                                    children: s.observedUltimateCompletion || 'Unknown'
                                }),
                                /*#__PURE__*/ _jsx("td", {
                                    children: s.potentialRecompeteHorizon || 'Unknown'
                                }),
                                /*#__PURE__*/ _jsx("td", {
                                    children: s.teamTimingHypothesis || 'Unknown'
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    s.horizonBasis,
                    ". This is not a predicted solicitation date."
                ]
            }),
            [
                [
                    'Current',
                    s.currentSource
                ],
                [
                    'Ultimate',
                    s.ultimateSource
                ]
            ].map(([label, source])=>/*#__PURE__*/ _jsxs("p", {
                    children: [
                        label,
                        " source:",
                        ' ',
                        source ? `${source.source} · ${source.sourceVersion} · ${source.sourceLocation} · Effective as of: ${source.effectiveAt?.toDate().toISOString() || 'Unknown'} · Retrieved: ${source.retrievedAt?.toDate().toISOString() || 'Unknown'} · ${source.valueState}` : 'Unknown — no selected source'
                    ]
                }, label)),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Team timing hypothesis",
                htmlFor: "award-hypothesis",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    id: "award-hypothesis",
                    type: "date",
                    "data-testid": Testids.awardSignal.hypothesis,
                    value: date,
                    onChange: (e)=>setDate(e.target.value)
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Planning rationale",
                htmlFor: "award-note",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    id: "award-note",
                    "data-testid": Testids.awardSignal.note,
                    value: note,
                    onChange: (e)=>setNote(e.target.value)
                })
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.awardSignal.save,
                disabled: save.isPending || !!date && !note.trim(),
                onClick: ()=>save.mutate(),
                children: save.isPending ? 'Saving…' : 'Save timing hypothesis'
            }),
            save.error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Could not save timing hypothesis. Check your access and retry."
            })
        ]
    });
}
